/**
 * Date utility functions for handling date formatting and conversions
 * without timezone issues
 */

/**
 * Converts a Date object to local YYYY-MM-DD string without timezone conversion
 * @param {Date} date - Date object to convert
 * @returns {string} Date string in YYYY-MM-DD format
 */
export const dateToLocalString = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

/**
 * Formats a date string (YYYY-MM-DD) to DDMMYYYY format for API calls
 * Handles timezone correctly by parsing as local date
 * @param {string} dateString - Date string in YYYY-MM-DD format
 * @returns {string|null} Date string in DDMMYYYY format or null if invalid
 */
export const formatDateToDDMMYYYY = (dateString) => {
  if (!dateString) return null;

  // Parse as local date to avoid timezone issues
  // Don't use new Date(dateString) as it parses as UTC
  const parts = dateString.split('-');
  if (parts.length !== 3) return null;

  const date = new Date(
    parseInt(parts[0], 10),      // year
    parseInt(parts[1], 10) - 1,  // month (0-indexed)
    parseInt(parts[2], 10)       // day
  );

  // Check if date is valid
  if (isNaN(date.getTime())) return null;

  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = date.getFullYear();

  return `${day}${month}${year}`;
};

/**
 * Gets date for N days ago from today
 * @param {number} days - Number of days to go back
 * @returns {string} Date string in YYYY-MM-DD format
 */
export const getDaysAgo = (days) => {
  const date = new Date();
  date.setDate(date.getDate() - days);
  return dateToLocalString(date);
};

/**
 * Gets today's date as local string
 * @returns {string} Today's date in YYYY-MM-DD format
 */
export const getTodayLocalString = () => {
  return dateToLocalString(new Date());
};
